import { CodeStatus, EmbeddedCodeLike } from '../entities/shared/code';
import { EntitySubStatus } from '../entities/entity.status/entity.status';
import { StatusSource, StatusStampOptions, buildStatusStamp, resolveStatusIdentity } from '../entities/entity.status/entity.status.stamp';
import { EntityStatusType } from '../entities/entity.status/enum';

const CODE_STATUSES = new Set<string>(Object.values(CodeStatus));
const ACTIVE_ITEM_CODE_STATUSES = new Set<CodeStatus>([CodeStatus.Normal, CodeStatus.Variation]);

/** Narrows the status legacy bridge to the Item domain's CodeStatus contract, reading the parent first. */
export function resolveItemCodeStatus(status: StatusSource | null | undefined): CodeStatus | undefined {

    const legacyStatus = resolveStatusIdentity(status)?.legacyStatus;

    return typeof legacyStatus === 'string' && CODE_STATUSES.has(legacyStatus)
        ? legacyStatus as CodeStatus
        : undefined;
}

/**
 * Returns the CodeStatus for an enabled Active Item status, limited to normal inline transitions.
 *
 * Pass a status read from the status set, never a stamp off a line. A stamp carries no `active` or
 * `parent`, so `undefined !== false` passes and the enabled check silently succeeds. Every caller
 * today fetches from `EntityStatusService`; the check tightens in E1-05 once the data shape is settled.
 */
export function resolveActiveItemCodeStatus(status: EntitySubStatus | null | undefined): CodeStatus | undefined {

    const codeStatus = resolveItemCodeStatus(status);

    return status?.active !== false && status?.parent?.active !== false && status?.statusType === EntityStatusType.Active && codeStatus && ACTIVE_ITEM_CODE_STATUSES.has(codeStatus)
        ? codeStatus
        : undefined;
}

/** Returns the CodeStatus for an enabled Inactive Item status used by lifecycle transitions. */
export function resolveInactiveItemCodeStatus(status: EntitySubStatus | null | undefined): CodeStatus | undefined {

    return status?.active !== false && status?.parent?.active !== false && status?.statusType === EntityStatusType.Inactive
        ? resolveItemCodeStatus(status)
        : undefined;
}

/**
 * Canonical stamp for an authorised-array Item/SOR, including its CodeStatus compatibility mirror.
 *
 * Identity only, plus whatever the caller recorded about the change — the reason, the comment, who made
 * it. Without `options` this rebuilt the stamp from the status alone, so a cancel that asked for a reason
 * had it dropped on the way through the write path.
 *
 * What the status permits is `editableBy` on the status itself, read at the point of the write; the
 * client lock belongs to the integration that set it and is never written from here.
 */
export function buildAuthorisedItemStatusStamp(status: EntitySubStatus, options?: StatusStampOptions): Partial<Pick<EmbeddedCodeLike, 'itemStatus' | 'status'>> {

    return {
        itemStatus: buildStatusStamp(status, options),
        status: resolveItemCodeStatus(status),
    };
}

/**
 * The entity substatus a legacy CodeStatus maps to, or undefined when the set has none.
 *
 * `systemName` is tried before `legacyStatus` because the two are not one-to-one: a tenant may point
 * several substatuses at the same legacy value — the seeded Item set has both `Cancelled` and `Deleted`
 * on `legacyStatus: 'Cancelled'`. Matching either field in a single pass returns whichever the query
 * happened to yield first, so cancelling a line could stamp it "Deleted".
 */
export function matchSubStatusByCodeStatus(statuses: EntitySubStatus[], status: CodeStatus | string | null | undefined): EntitySubStatus | undefined {

    if (!status || !statuses?.length) return undefined;

    return statuses.find(candidate => resolveStatusIdentity(candidate)?.systemName === status)
        ?? statuses.find(candidate => resolveStatusIdentity(candidate)?.legacyStatus === status);
}

/** A created authorised line's Item status: the supplied one when the tenant has it enabled, else the enabled match for its CodeStatus, else none, so a creator is never refused. */
export function createdLineItemStatusStamp(line: Partial<Pick<EmbeddedCodeLike, 'itemStatus' | 'status'>>, statuses: EntitySubStatus[]): Partial<Pick<EmbeddedCodeLike, 'itemStatus' | 'status'>> {

    const usable = (statuses ?? []).filter(status => !!(resolveActiveItemCodeStatus(status) ?? resolveInactiveItemCodeStatus(status)));
    const itemStatus = usable.find(status => !!line.itemStatus?._id && status._id === line.itemStatus._id)
        ?? matchSubStatusByCodeStatus(usable, line.status ?? CodeStatus.Normal);

    if (!itemStatus) return { itemStatus: undefined, status: line.status };

    const stamp = buildAuthorisedItemStatusStamp(itemStatus);
    return { itemStatus: stamp.itemStatus, status: stamp.status ?? line.status };
}

/** True when a created authorised line arrived with no Item status, so its writer must stamp one; writers call it to skip the status lookup when every line already has one. */
export function lacksItemStatus(line: Partial<Pick<EmbeddedCodeLike, 'itemStatus'>>): boolean {

    return !line?.itemStatus?._id;
}

/**
 * Stamps the tenant's Item status on every created authorised line that arrived without one, for the stage
 * writers that receive lines from many callers (quote convert, templates, imports, bill and PO approve).
 *
 * A line that already carries an Item status is returned as it came: rebuilding it would drop the setBy, setAt
 * and reason its caller recorded, as on a moved line or an integration's own stamp (WBS-12149).
 */
export function stampLinesLackingItemStatus<T extends Partial<Pick<EmbeddedCodeLike, 'itemStatus' | 'status'>>>(lines: T[], statuses: EntitySubStatus[]): T[] {

    return (lines ?? []).map(line => lacksItemStatus(line) ? { ...line, ...createdLineItemStatusStamp(line, statuses) } : line);
}

/**
 * True when a pending line goes out as a NEW supplementary line, given the integration's
 * awaiting-submission status. The HomesNSW handler sends exactly these lines and the request dialog
 * previews exactly these lines (WBS-13013); change one and both move. No status means nothing is sent.
 */
export function isAwaitingSupplementarySubmission(line: Partial<EmbeddedCodeLike>, awaitingSubmissionStatus: string | null | undefined): boolean {

    return !!awaitingSubmissionStatus && line?.itemStatus?.systemName === awaitingSubmissionStatus;
}

/**
 * True when a stage item has been taken off the stage but can still come back.
 *
 * Removal is reversible, so the item task tree is deactivated rather than destroyed and the
 * photo evidence and completed compliance hanging off it survive an un-cancel. Claim items
 * carry the same meaning on itemStatus instead of status.
 */
export function isItemRemoved(item: Partial<EmbeddedCodeLike>, isClaimCode: boolean): boolean {

    if (isClaimCode) {
        return item?.itemStatus?.statusType === EntityStatusType.Inactive;
    }

    // Authorised lines may express cancel via Item (SOR) entity status and/or legacy CodeStatus.
    const entityCancelled = item?.itemStatus?.legacyStatus === CodeStatus.Cancelled
        || item?.itemStatus?.systemName === CodeStatus.Cancelled
        || item?.itemStatus?.statusType === EntityStatusType.Inactive;

    return item?.status === CodeStatus.Cancelled || item?.status === CodeStatus.Deleted || entityCancelled;
}
